/**
 * Chapter016.js — Plant & Equipment Register (OHSE 016)
 * Migrated from SWMS Platform Chapter016.tsx → JavaScript
 *
 * Props:
 *   data        {array}   equipment_entries from swmsData
 *   onChange    {fn}      (newEntries) => void
 *   companyName {string}  from companyInfo.name
 */

import { useCallback } from 'react';
import { Plus, Trash2 } from 'lucide-react';

const inputCls = 'w-full px-2 py-1.5 text-sm bg-transparent border border-[#e0dbd0] rounded text-[#1a1a2e] placeholder:text-gray-400 focus:outline-none focus:border-[#c9a84c] focus:bg-white transition-all';
const selectCls = 'w-full h-9 px-2 text-sm border border-[#e0dbd0] rounded bg-white text-[#1a1a2e] focus:outline-none focus:border-[#c9a84c]';

function emptyEntry() {
  return {
    plant_type: '',
    serial_registration_number: '',
    make_model: '',
    authority_registration_required: '',
    authority_registration_expiry: '',
    last_service_date: '',
    maintenance_frequency: '',
    alteration_details: '',
    date_on_site: '',
    logbook_available: false,
  };
}

export default function Chapter016({ data = [], onChange, companyName }) {
  const cn = companyName || '[Company Name]';

  const addEntry = useCallback(() => onChange([...data, emptyEntry()]), [data, onChange]);
  const removeEntry = useCallback((idx) => onChange(data.filter((_, i) => i !== idx)), [data, onChange]);
  const updateEntry = useCallback((idx, field, value) => {
    const updated = [...data];
    updated[idx] = { ...updated[idx], [field]: value };
    onChange(updated);
  }, [data, onChange]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest mb-1">OHSE 016</p>
        <h2 className="text-xl font-bold text-[#1a1a2e] uppercase tracking-wide">Plant &amp; Equipment Register</h2>
      </div>

      {/* Declaration */}
      <div className="p-4 bg-[#f8f7f4] rounded-lg border border-[#e0dbd0] text-sm text-[#1a1a2e] leading-relaxed">
        The following register contains details of all plant and equipment to be used by{' '}
        <span className="font-semibold text-[#c9a84c]">{cn}</span>{' '}
        during the course of the work activities. Examples include lifting gear, fire-fighting
        equipment, mobile plant, fall restraint equipment and other.
      </div>

      {/* Equipment Cards */}
      {data.map((entry, idx) => (
        <EquipmentCard
          key={idx}
          entry={entry}
          index={idx}
          onUpdate={(field, value) => updateEntry(idx, field, value)}
          onRemove={() => removeEntry(idx)}
        />
      ))}

      {data.length === 0 && (
        <div className="flex flex-col items-center justify-center py-10 text-center border border-[#e0dbd0] rounded-lg bg-white">
          <p className="text-sm text-gray-400 mb-3">No equipment registered yet.</p>
          <button type="button" onClick={addEntry}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-[#1a1a2e] rounded hover:bg-[#252547] transition-colors">
            <Plus className="w-4 h-4" /> Add Equipment
          </button>
        </div>
      )}

      {data.length > 0 && (
        <div className="flex justify-center">
          <button type="button" onClick={addEntry}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-[#1a1a2e] rounded hover:bg-[rgba(201,168,76,0.15)] border border-[#c9a84c]/40 transition-colors">
            <Plus className="w-4 h-4" /> Add Equipment
          </button>
        </div>
      )}
    </div>
  );
}

function EquipmentCard({ entry, index, onUpdate, onRemove }) {
  const inputCls2 = 'w-full px-2 py-1.5 text-sm bg-transparent border border-[#e0dbd0] rounded text-[#1a1a2e] placeholder:text-gray-400 focus:outline-none focus:border-[#c9a84c] focus:bg-white transition-all';
  const selectCls2 = 'w-full h-9 px-2 text-sm border border-[#e0dbd0] rounded bg-white text-[#1a1a2e] focus:outline-none focus:border-[#c9a84c]';

  return (
    <div className="relative group bg-white border border-[#e0dbd0] rounded-lg p-5 hover:shadow-sm transition-shadow">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 pb-2 border-b border-[#e0dbd0]">
        <span className="flex items-center gap-2">
          <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#1a1a2e] text-white text-xs font-semibold shrink-0">
            {index + 1}
          </span>
          <span className="text-sm font-semibold text-[#1a1a2e]">
            {entry.plant_type || 'Unnamed Equipment'}
          </span>
        </span>
        <button type="button" onClick={onRemove}
          className="p-1 text-red-400 hover:text-red-600 transition-colors">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-x-8 gap-y-4">
        {/* Left column */}
        <div className="space-y-4">
          <Field label="Plant Type">
            <input type="text" value={entry.plant_type} placeholder="e.g. Excavator"
              onChange={e => onUpdate('plant_type', e.target.value)} className={inputCls2} />
          </Field>
          <Field label="Serial No. / Registration No.">
            <input type="text" value={entry.serial_registration_number} placeholder="Serial / Registration #"
              onChange={e => onUpdate('serial_registration_number', e.target.value)} className={inputCls2} />
          </Field>
          <Field label="Make / Model">
            <input type="text" value={entry.make_model} placeholder="Make / Model"
              onChange={e => onUpdate('make_model', e.target.value)} className={inputCls2} />
          </Field>
          <Field label="Registration with Authority Required? (Y/N)">
            <select value={entry.authority_registration_required}
              onChange={e => onUpdate('authority_registration_required', e.target.value)} className={selectCls2}>
              <option value="">— Select —</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </Field>
          <Field label="Authority Registration Expiry Date">
            <input type="date" value={entry.authority_registration_expiry}
              onChange={e => onUpdate('authority_registration_expiry', e.target.value)} className={inputCls2} />
          </Field>
        </div>

        {/* Right column */}
        <div className="space-y-4">
          <Field label="Date Last Service or Maintenance Record Available">
            <input type="date" value={entry.last_service_date}
              onChange={e => onUpdate('last_service_date', e.target.value)} className={inputCls2} />
          </Field>
          <Field label="Required Maintenance Frequency">
            <input type="text" value={entry.maintenance_frequency} placeholder="e.g. Monthly, Quarterly"
              onChange={e => onUpdate('maintenance_frequency', e.target.value)} className={inputCls2} />
          </Field>
          <Field label="Alteration Details (Y/N/NA)">
            <select value={entry.alteration_details}
              onChange={e => onUpdate('alteration_details', e.target.value)} className={selectCls2}>
              <option value="">— Select —</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
              <option value="N/A">N/A</option>
            </select>
          </Field>
          <Field label="Date on Site">
            <input type="date" value={entry.date_on_site}
              onChange={e => onUpdate('date_on_site', e.target.value)} className={inputCls2} />
          </Field>
          <Field label="Logbook Available">
            <div className="flex items-center gap-3 h-9">
              <input type="checkbox" checked={entry.logbook_available}
                onChange={e => onUpdate('logbook_available', e.target.checked)}
                className="w-4 h-4 rounded border-[#e0dbd0] cursor-pointer" />
              <span className="text-sm text-gray-500">{entry.logbook_available ? 'Yes' : 'No'}</span>
            </div>
          </Field>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
